import { useId, useRef } from "react";
import type { CSSProperties, ReactNode } from "react";

import { noReceipt } from "../lib/noReceipt";
import styles from "./Panel.module.css";
import { useScrollableRegionFocus } from "./useScrollableRegionFocus";

interface PanelProps {
  title: string;
  /** The heading level this panel's title takes.
   *
   * **Default `h2`, because a signed-in screen already has an `h1`** — `AppShell` renders the
   * screen's name as one in the top bar — and a second `h1` per panel would flatten the document
   * into a list of equal headings.
   *
   * **The auth screens pass `h1`**: they are outside the shell, so nothing above them provides
   * one, and the panel's own title *is* the page's title. Before this existed every panel in the
   * product rendered its title as a `<div>`, so the sign-in and MFA pages had **no heading element
   * of any level** and a screen reader met a page with no structure at all. `/join` had always
   * done it correctly, which is what showed this was an oversight rather than a decision. */
  as?: "h1" | "h2" | "h3";
  sub?: string;
  /** Why the quantity printed in `sub` has no receipt of its own (`lib/noReceipt.ts`). Only for a
   * subtitle that prints one. */
  subNoReceipt?: string;
  action?: ReactNode;
  /** Grid placement, e.g. `"1 / 8"`. Kept as a prop so a screen owns its layout and the panel
   * stays reusable. */
  column?: string;
  row?: string;
  flush?: boolean;
  children: ReactNode;
}

export function Panel({
  title,
  as: Heading = "h2",
  sub,
  subNoReceipt,
  action,
  column,
  row,
  flush,
  children,
}: PanelProps) {
  const body = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useScrollableRegionFocus(body, { labelledBy: titleId });
  return (
    <section
      className={styles.panel}
      style={
        {
          "--panel-col": column,
          "--panel-row": row,
        } as CSSProperties
      }
    >
      <header className={styles.head}>
        <div className={styles.headText}>
          {/* The subtitle is a **sibling** of the heading rather than inside it. Inside, it would
              join the heading's accessible name, so "Set up your second factor" would be announced
              as "Set up your second factor Required for this role" — the heading stops being the
              short label a screen reader's heading list is for. `.headTitle` resets the element's
              own margin and type so this changed no pixel. */}
          <Heading id={titleId} className={styles.headTitle}>
            {title}
          </Heading>
          {sub ? (
            <span className={styles.sub} {...(subNoReceipt ? noReceipt(subNoReceipt) : {})}>
              {sub}
            </span>
          ) : null}
        </div>
        {action}
      </header>
      <div ref={body} className={`${styles.body} ${flush ? styles.bodyFlush : ""}`}>
        {children}
      </div>
    </section>
  );
}

/** A sentence that says the product is still working ("Loading the categories…"). Twenty-odd call
 * sites spell a loading state as `<Empty>Loading…</Empty>`; drawn but not announced, it is a loading
 * state that does not exist for a screen-reader listener (gate 37). So the sentence itself decides:
 * a string that begins with a working verb and ends in an ellipsis is a status region, and `busy`
 * overrides in either direction for a caller whose wording does not fit. */
const WORKING = /^(Loading|Reading|Checking)\b[^.]*…$/;

export function Empty({ children, busy }: { children: ReactNode; busy?: boolean }) {
  const working = busy ?? (typeof children === "string" && WORKING.test(children));
  return (
    <p className={styles.empty} role={working ? "status" : undefined}>
      {children}
    </p>
  );
}
